<script>
export const reuseComponent = true
</script>

<script setup>
import { computed } from 'vue'
import { frames } from '../../../utils/frames'
import CharacterEnlarge from '../kijk_live_qmusic_nl_default/text_animations/CharacterEnlarge.vue'
import CharacterJumpFromTheRight from '../kijk_live_qmusic_nl_default/text_animations/CharacterJumpFromTheRight.vue'
import VideoPlayer from '../../VideoPlayer.vue'
const gsapTimeline = useGsapTimeline()

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})

const activeItem = ref(props.item)

const subtitle = computed(() => {
  if (activeItem.value.enrichment.message.from?.name) {
    return `van ${activeItem.value.enrichment.message.from.name}`
  }
  return 'Via de Q-app'
})

const timelineTitle = ref(null)
const timelineSubtitle = ref(null)

const titleElement = ref(null)
const subtitleElement = ref(null)
const bubbleElement = ref(null)
const imageCardElement = ref(null)

let startTimeline = () =>
  gsapTimeline({ name: 'AppMessageHighlightVideoWall' })
    .add(timelineTitle)
    .add(timelineSubtitle, '<.48')
    .fromTo(bubbleElement, { scale: 0, duration: frames(7), transformOrigin: 'bottom right', ease: 'power2.out' }, { scale: 1 }, '=-.16')
    .fromTo(imageCardElement, { x: -2500, rotate: -30, duration: frames(7), transformOrigin: 'bottom left', ease: 'power2.out' }, { x: 0, rotate: -2 }, '<.16')

onMounted(() => startTimeline())

watch(
  () => props.item,
  () => {
    gsapTimeline({ name: 'AppMessageHighlightVideoWall - Switch' })
      .to(subtitleElement, { duration: frames(8), ease: 'power.in', y: 400 }, '<')
      .to(bubbleElement, { duration: frames(8), ease: 'power2.out', scale: 0, opacity: 0 }, '<')
      .to(imageCardElement, { duration: frames(8), ease: 'power2.out', y: 1000, x: 0, rotate: -2, opacity: 0 }, '<')
      .add(() => (activeItem.value = props.item))
      .set(titleElement, { y: 0, opacity: 1 })
      .set(subtitleElement, { x: 0, opacity: 1, y: -400 })
      .set(bubbleElement, { opacity: 1, scale: 0 })
      .set(imageCardElement, { opacity: 1, x: -2500, rotate: -30, y: 0 })
      .to(subtitleElement, { duration: frames(8), ease: 'power.out', y: 0 }, '<')
      .to(bubbleElement, { scale: 1, duration: frames(7), transformOrigin: 'bottom right', ease: 'power2.out' }, '<')
      .to(imageCardElement, { x: 0, rotate: -2, duration: frames(7), transformOrigin: 'bottom left', ease: 'power2.out' }, '<.24')
  }
)

const { onDisappear } = useDisappearable()
onDisappear(async () => {
  const finishTimeline = startTimeline()
  finishTimeline.reverse()

  return await gsapTimeline({ name: 'AppMessageHighlightVideoWall - End' }).add(finishTimeline)
})

const showMedia = computed(() => activeItem.value.enrichment.message.image || activeItem.value.enrichment.message.video)
</script>

<template>
  <div class="relative size-full q-egg:px-egg">
    <div class="relative size-full">
      <div v-show="showMedia" class="absolute top-20 q-corner:top-[650px] q-egg:top-72 5tv:top-40 left-20 q-egg:left-[16rem] 5tv:left-40 bottom-20 q-egg:bottom-[16rem] 5tv:bottom-40">
        <div
          ref="imageCardElement"
          class="border-[16px] 5tv:border-[32px] border-b-[80px] 5tv:border-b-[160px] border-white h-full q-corner:max-h-[3000px] max-w-[800px] q-corner:max-w-[2000px] q-egg:max-w-[900px] 5tv:max-w-[2000px] -rotate-2 origin-bottom-left"
        >
          <img v-if="activeItem.enrichment.message.image" :src="activeItem.enrichment.message.image" class="size-full object-cover" />
          <VideoPlayer v-if="activeItem.enrichment.message.video" :src="activeItem.enrichment.message.video" autoplay muted loop class="size-full object-cover" />
        </div>
      </div>
      <div
        class="absolute q-corner:gap-20 top-20 q-corner:top-5 5tv:top-40 q-egg:top-64 q-corner:left-5 right-20 q-corner:right-5 q-egg:right-52 5tv:right-40 bottom-20 5tv:bottom-40 text-right flex flex-col q-corner:flex-row-reverse q-corner:items-start justify-between q-egg:justify-normal items-end q-corner:items-[normal]"
      >
        <div class="flex flex-col items-end flex-shrink-0">
          <h1 ref="titleElement" class="text-[10rem] 5tv:text-[30rem] q-corner:text-[15rem] font-bold text-white font-cervo leading-none">
            <CharacterJumpFromTheRight text="Bericht" textSizeClass="text-[1em]" textOutlineSizeClass="text-[1.6em]" @timeline="timelineTitle = $event" />
          </h1>
          <div class="overflow-y-hidden pt-8">
            <h1 ref="subtitleElement" class="text-[6rem] 5tv:text-[18.1rem] q-corner:text-[16rem] text-white -mt-10 font-cervo leading-none">
              <CharacterEnlarge :text="subtitle" @timeline="timelineSubtitle = $event" />
            </h1>
          </div>
        </div>
        <div
          v-show="activeItem.enrichment.message.message"
          ref="bubbleElement"
          class="bg-white p-12 py-6 q-egg:py-10 5tv:p-24 q-corner:py-12 q-corner:px-18 q-egg:mt-12 rounded-[4rem] 5tv:rounded-[6rem] relative text-5xl 5tv:text-[7rem] q-corner:text-[6rem] leading-tight max-w-[900px] q-corner:max-w-none q-egg:max-w-[680px] 5tv:max-w-[2300px] font-qarla text-left mb-24"
          :class="{ 'q-egg:text-6xl q-egg:max-w-[1300px] q-egg:mt-32': !showMedia }"
        >
          <p class="line-clamp-[8] q-egg:line-clamp-[11] 5tv:line-clamp-5" :class="{ 'q-corner:line-clamp-[30]': !showMedia }" v-text="activeItem.enrichment.message.message" />
          <div class="absolute -bottom-14 5tv:-bottom-20 right-10 bg-white size-16 5tv:size-24" :style="{ clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 0%)' }" />
        </div>
      </div>
    </div>
  </div>
</template>
